import { gql } from '@apollo/client';
import { SERVICE_CATALOG_FIELDS } from '@/graphql/fragments/services';

// Catalog services for the Services management grid. `where` is built by the caller: own client for
// regular users, or the whole org (via client.user.organization_id) for carrier admins.
export const QUERY_SERVICE_CATALOG = gql`
    ${SERVICE_CATALOG_FIELDS}

    query ServiceCatalogByScope($where: service_catalog_bool_exp!) {
        service_catalog(where: $where, order_by: [{ service_code: asc }, { dimension_key: asc }, { service_id: asc }]) {
            ...ServiceCatalogFields
            client {
                client_id
                business_name
            }
        }
    }
`;

// The catalog write, shared by both upserts below so their update_columns can't drift. Needs
// SERVICE_CATALOG_FIELDS and an $object variable from the operation it's spliced into.
const UPSERT_SERVICE_CATALOG_FIELD = `
    insert_service_catalog_one(
        object: $object
        # Must stay in sync with buildServiceCatalogPayload's keys — a field missing here
        # saves on create and silently no-ops on edit. dimensions is the one deliberate
        # omission: scope is create-only (it's half the row's uniqueness constraint), so it
        # inserts and is ignored on update, matching the read-only field in the modal.
        on_conflict: {
            constraint: service_catalog_pkey
            update_columns: [service_code, description, service_type, size, require_confirm, billable, service_time]
        }
    ) {
        ...ServiceCatalogFields
    }
`;

export const UPSERT_SERVICE_CATALOG = gql`
    ${SERVICE_CATALOG_FIELDS}

    mutation UpsertServiceCatalog($object: service_catalog_insert_input!) {
        ${UPSERT_SERVICE_CATALOG_FIELD}
    }
`;

// A catalog write that also sets the service's rate on the shipper's GE tariffs, in one transaction:
// clears the service's SERVICE_RATE rows (stale_rates), then inserts the new ones. Built by
// buildServiceRateChange, which never leaves stale_rates unanchored.
export const UPSERT_SERVICE_CATALOG_WITH_RATES = gql`
    ${SERVICE_CATALOG_FIELDS}

    mutation UpsertServiceCatalogWithRates(
        $object: service_catalog_insert_input!
        $stale_rates: rates_bool_exp!
        $rates: [rates_insert_input!]!
    ) {
        ${UPSERT_SERVICE_CATALOG_FIELD}
        delete_rates(where: $stale_rates) {
            affected_rows
        }
        insert_rates(objects: $rates) {
            affected_rows
        }
    }
`;

export const DELETE_SERVICE_CATALOG = gql`
    mutation DeleteServiceCatalog($service_id: uuid!) {
        delete_service_catalog_by_pk(service_id: $service_id) {
            service_id
        }
    }
`;

// The tariffs a catalog service's rate goes on: every GE tariff the shipper's shipper rate prices
// from, matching the filter pricing loads shipper overrides with (onward-functions
// graphql/queries/pricing_overrides.js, minus the carrier, so every partner's tariff is included).
export const QUERY_SERVICE_RATE_TARIFFS = gql`
    query ServiceRateTariffs($client_id: uuid!) {
        pricing_overrides(
            where: {
                _and: [
                    { client_id: { _eq: $client_id } }
                    { algo_type: { _eq: "GENERAL_ELECTRIC" } }
                    { oms: { _eq: false } }
                    { override_type: { _in: ["E2E", "FINAL_MILE", "GIG"] } }
                    { enabled: { _eq: true } }
                    { tariff_id: { _is_null: false } }
                ]
            }
        ) {
            pricing_override_id
            tariff_id
        }
    }
`;

// The SERVICE_RATE rows on those tariffs: the rates set for catalog services.
export const QUERY_SERVICE_RATES = gql`
    query ServiceRates($tariff_ids: [uuid!]!) {
        rates(where: { tariff_id: { _in: $tariff_ids }, type: { _eq: "SERVICE_RATE" } }) {
            tariff_id
            rate
            qbo_tag
            dimensions
        }
    }
`;
